import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  Phone, 
  Calendar, 
  User, 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  X,
  ExternalLink,
  CreditCard
} from 'lucide-react';
import { adminQuotesAPI } from '../../services/api';

export const AdminQuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const data = await adminQuotesAPI.list();
      // Trier par date de création (plus récent en premier)
      const sortedQuotes = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setQuotes(sortedQuotes);
    } catch (err) {
      setError('Erreur lors du chargement des devis');
      console.error('Error loading quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await adminQuotesAPI.updateStatus(quoteId, status);
      await loadQuotes(); // Recharger la liste
    } catch (err) {
      console.error('Error updating quote status:', err);
    }
  };

  const deleteQuote = async (quoteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        await adminQuotesAPI.delete(quoteId);
        await loadQuotes(); // Recharger la liste
      } catch (err) {
        console.error('Error deleting quote:', err);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-800' },
      completed: { label: 'Terminé', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800' }
    };
    
    const statusInfo = statusMap[status] || statusMap.pending;
    return (
      <Badge className={statusInfo.color}>
        {statusInfo.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generatePaymentLink = async (quoteId) => {
    const amount = prompt('Entrez le montant du devis (en EUR):');
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      alert('❌ Montant invalide\n\nLe montant doit être un nombre positif supérieur à zéro.');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/payments/admin/generate-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa('znsolar_admin:ZN2025Solar!')
        },
        body: JSON.stringify({
          quote_id: quoteId,
          amount: parseFloat(amount),
          currency: 'EUR',
          description: `Devis Z&N Solar - ${quoteId}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la génération du lien');
      }

      const data = await response.json();
      
      // Afficher le lien et essayer de le copier dans le presse-papiers
      const paymentLink = data.payment_link;
      
      // Essayer de copier dans le presse-papiers avec gestion d'erreur
      let copied = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(paymentLink);
          copied = true;
        }
      } catch (err) {
        console.log('Clipboard access denied, using fallback');
        copied = false;
      }
      
      // Afficher le lien avec message approprié
      if (copied) {
        alert(`✅ Lien de paiement généré et copié!\n\nMontant: ${amount}€\n\n✅ Le lien a été copié dans le presse-papiers.\n\nEnvoyez ce lien au client:\n${paymentLink}`);
      } else {
        // Fallback: utiliser prompt qui permet la sélection manuelle
        const userAction = prompt('✅ Lien de paiement généré!\n\nMontant: ' + amount + '€\n\n⚠️ Copie automatique non disponible.\nSélectionnez et copiez ce lien (Ctrl+C ou Cmd+C):', paymentLink);
      }

      await loadQuotes(); // Recharger pour voir le statut mis à jour
    } catch (err) {
      console.error('Error generating payment link:', err);
      alert('❌ Erreur lors de la génération du lien de paiement\n\n' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p>Chargement des devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-red-800 text-center">
              <p className="font-medium">Erreur</p>
              <p className="text-sm mt-1">{error}</p>
              <Button 
                onClick={loadQuotes} 
                variant="outline" 
                className="mt-4 border-red-200 text-red-800 hover:bg-red-100"
              >
                Réessayer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Demandes de Devis</h2>
          <p className="text-gray-600 mt-1">
            {quotes.length} devis reçu{quotes.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={loadQuotes} variant="outline">
          Actualiser
        </Button>
      </div>

      {/* Quotes List */}
      {quotes.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Aucun devis reçu</p>
              <p className="text-sm mt-1">Les nouveaux devis apparaîtront ici</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <Card key={quote.id} className="border-l-4 border-l-yellow-500">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <CardTitle className="text-lg">{quote.name}</CardTitle>
                      <p className="text-sm text-gray-600 font-medium">{quote.projectType}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(quote.status)}
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuoteStatus(quote.id, 'completed')}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteQuote(quote.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <a 
                      href={`mailto:${quote.email}`}
                      className="text-blue-600 hover:underline"
                    >
                      {quote.email}
                    </a>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </div>
                  
                  {quote.phone && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <a 
                        href={`tel:${quote.phone}`}
                        className="text-blue-600 hover:underline"
                      >
                        {quote.phone}
                      </a>
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span>{formatDate(quote.created_at)}</span>
                  </div>
                </div>

                {/* Message */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <MessageSquare className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 mb-1">Message du client :</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{quote.message}</p>
                    </div>
                  </div>
                </div>

                {/* Simulateur de Puissance */}
                {quote.appliancesData && quote.appliancesData.appliances && quote.appliancesData.appliances.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">⚡</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-800 mb-3">Simulateur de Puissance - Appareils :</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-yellow-100">
                              <tr>
                                <th className="px-2 py-1 text-left font-semibold">Appareil</th>
                                <th className="px-2 py-1 text-center font-semibold">Qté</th>
                                <th className="px-2 py-1 text-right font-semibold">Puiss. (W)</th>
                                <th className="px-2 py-1 text-right font-semibold">Temps (h/j)</th>
                                <th className="px-2 py-1 text-right font-semibold">Énergie (Wh)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quote.appliancesData.appliances.map((app, idx) => (
                                <tr key={idx} className="border-t border-yellow-200">
                                  <td className="px-2 py-1">{app.name}</td>
                                  <td className="px-2 py-1 text-center">{app.quantity}</td>
                                  <td className="px-2 py-1 text-right">{app.power} W</td>
                                  <td className="px-2 py-1 text-right">{app.hours}</td>
                                  <td className="px-2 py-1 text-right font-semibold">{app.totalWh} Wh</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {quote.appliancesData.totals && (
                          <div className="mt-3 p-3 bg-yellow-100 rounded border border-yellow-300">
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div>
                                <span className="font-semibold">Puissance totale:</span>
                                <div className="text-lg font-bold text-orange-600">{quote.appliancesData.totals.totalPower} W</div>
                              </div>
                              <div>
                                <span className="font-semibold">Consommation/jour:</span>
                                <div className="text-lg font-bold text-orange-600">{quote.appliancesData.totals.totalDailyWh} Wh</div>
                              </div>
                              <div>
                                <span className="font-semibold">Batterie recommandée:</span>
                                <div className="text-lg font-bold text-orange-600">{quote.appliancesData.totals.batteryAh} Ah (12V)</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    onClick={() => updateQuoteStatus(quote.id, 'in_progress')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Marquer en cours
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={() => generatePaymentLink(quote.id)}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:from-yellow-600 hover:to-yellow-700"
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Générer lien PayPal
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`mailto:${quote.email}?subject=Re: Votre demande de devis Z&N Solar&body=Bonjour ${quote.name},%0D%0A%0D%0ANous avons bien reçu votre demande de devis concernant: ${quote.projectType}%0D%0A%0D%0ACordialement,%0D%0AÉquipe Z&N Solar`, '_blank')}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Répondre par email
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminQuotesPage;